'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { EmblaCarouselType, EmblaEventType, EmblaOptionsType } from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';
import { usePrevNextButtons } from './EmblaCarouselArrowButtons';
import { NewsItem } from '@/types/newsSection';

const TWEEN_FACTOR_BASE = 0.84;

const numberWithinRange = (number: number, min: number, max: number): number =>
  Math.min(Math.max(number, min), max);

type PropType = {
  items: NewsItem[];
  options?: EmblaOptionsType;
};

const OurNewsEmblaCarousel: React.FC<PropType> = (props) => {
  const { items, options } = props;
  const defaultOptions: EmblaOptionsType = {
    align: 'center',
    containScroll: 'trimSnaps',
    loop: true,
    dragFree: false,
    startIndex: 2,
    slidesToScroll: 1,
  };
  const [emblaRef, emblaApi] = useEmblaCarousel({ ...defaultOptions, ...options });
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const tweenFactor = useRef(0);

  usePrevNextButtons(emblaApi);

  const onScroll = useCallback((emblaApi: EmblaCarouselType) => {
    const progress = Math.max(0, Math.min(1, emblaApi.scrollProgress()));
    setScrollProgress(progress * 100);
  }, []);

  const setTweenFactor = useCallback((emblaApi: EmblaCarouselType) => {
    tweenFactor.current = TWEEN_FACTOR_BASE * emblaApi.scrollSnapList().length;
  }, []);

  const tweenOpacity = useCallback((emblaApi: EmblaCarouselType, eventName?: EmblaEventType) => {
    const engine = emblaApi.internalEngine();
    const scrollProgress = emblaApi.scrollProgress();
    const slidesInView = emblaApi.slidesInView();
    const isScrollEvent = eventName === 'scroll';

    emblaApi.scrollSnapList().forEach((scrollSnap, snapIndex) => {
      let diffToTarget = scrollSnap - scrollProgress;
      const slidesInSnap = engine.slideRegistry[snapIndex];

      slidesInSnap.forEach((slideIndex) => {
        if (isScrollEvent && !slidesInView.includes(slideIndex)) return;

        if (engine.options.loop) {
          engine.slideLooper.loopPoints.forEach((loopItem) => {
            const target = loopItem.target();

            if (slideIndex === loopItem.index && target !== 0) {
              const sign = Math.sign(target);

              if (sign === -1) {
                diffToTarget = scrollSnap - (1 + scrollProgress);
              }
              if (sign === 1) {
                diffToTarget = scrollSnap + (1 - scrollProgress);
              }
            }
          });
        }

        const tweenValue = 2 - Math.abs(diffToTarget * tweenFactor.current);
        const opacity = numberWithinRange(tweenValue, 0, 1).toString();
        emblaApi.slideNodes()[slideIndex].style.opacity = opacity;
      });
    });
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onScroll(emblaApi);
    setTweenFactor(emblaApi);
    tweenOpacity(emblaApi);
    emblaApi
      .on('reInit', onScroll)
      .on('scroll', onScroll)
      .on('slideFocus', onScroll)
      .on('reInit', setTweenFactor)
      .on('reInit', tweenOpacity)
      .on('scroll', tweenOpacity)
      .on('slideFocus', tweenOpacity);
  }, [emblaApi, onScroll, setTweenFactor, tweenOpacity]);

  // Auto-play functionality
  useEffect(() => {
    if (!emblaApi || !isPlaying || typeof window === 'undefined') return;

    const autoPlay = () => {
      emblaApi.scrollNext();
    };

    // eslint-disable-next-line no-undef
    const interval = setInterval(autoPlay, 2000);
    // eslint-disable-next-line no-undef
    return () => clearInterval(interval);
  }, [emblaApi, isPlaying]);

  // Pause on hover
  const handleMouseEnter = () => setIsPlaying(false);
  const handleMouseLeave = () => setIsPlaying(true);

  return (
    <div className="embla">
      <div
        className="embla__viewport"
        ref={emblaRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}>
        <div className="embla__container rounded-2xl text-white [&_.embla__slide]:flex-shrink-0 [&_.embla__slide]:px-1 [&_.embla__slide]:opacity-100">
          {items.map((item, index) => (
            <div className="embla__slide" key={item._id ?? index}>
              <div className="embla__slide__content relative h-80 w-full overflow-hidden rounded-2xl">
                {/* News Image */}
                <div className="h-full w-full">
                  <Image
                    src={item.coverImage.asset.url}
                    alt={item.coverImage.alt}
                    fill
                    className="object-cover"
                  />
                </div>
              </div>
              <div className="px-2 pt-4">
                <p className="line-clamp-3 text-xl font-medium text-white ltr:text-start rtl:text-end">
                  {item.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="embla__controls">
        <div className="embla__progress">
          <div
            className="embla__progress__bar"
            style={{ transform: `translate3d(${scrollProgress}%,0px,0px)` }}
          />
        </div>
      </div>
    </div>
  );
};

export default OurNewsEmblaCarousel;
